/**
 * The shapes a stacked bar chart is drawn from.
 *
 * Deliberately NOT the area chart's `ChartSeries`, which carries dated points because it draws a
 * continuous value. A bar chart's x-axis is a list of BUCKETS — discrete periods with no meaning
 * between them — so a bucket carries a map of segment values and nothing about dates at all.
 *
 * The caller decides what a bucket is. Income passes years or months; nothing here knows which, and
 * the label is given rather than derived so the component owns no formatting rule that a second
 * consumer might need differently.
 */

export interface BarSeries {
  /**
   * Stable across renders and unique. Income keys sources by id and one-off entries by category, so
   * two sources sharing a name — which is ordinary — stay two segments rather than collapsing into
   * one, last-writer-wins. That collapse is the defect feature 006 found in the area chart's first
   * real use, and it is worth not repeating.
   */
  key: string;
  label: string;
}

export interface BarBucket {
  key: string;
  label: string;
  /**
   * Incomplete by construction rather than by anything that happened — the current year, with months
   * still to come.
   *
   * It does NOT mean "draw this bucket faded". What has happened has happened, and fading the whole
   * bar says the arrived money is provisional, which is false. The incompleteness is expressed by
   * `projected` sitting above `values`, and by the label.
   */
  partial: boolean;
  /** Segment key → value that has actually occurred. A missing key is zero. Drawn solid. */
  values: Record<string, number>;
  /**
   * Segment key → value still expected in this bucket, drawn faded ABOVE the solid part.
   *
   * The caller decides what "expected" means and whether it is honest to show — this component only
   * knows that it goes on top and is drawn differently.
   */
  projected?: Record<string, number>;
}

/** Nothing to draw: no series, no buckets, or every value zero. */
export function isEmptyBars(series: readonly BarSeries[], buckets: readonly BarBucket[]): boolean {
  if (series.length === 0 || buckets.length === 0) return true;
  return buckets.every(
    (bucket) =>
      Object.values(bucket.values).every((value) => value === 0) &&
      Object.values(bucket.projected ?? {}).every((value) => value === 0),
  );
}
